import { TranslationService } from '../services/translation.service.js'
import { map, shareReplay } from 'rxjs/operators'
import { ActivatedRoute, Router } from '@angular/router'
import {
	OnInit,
	OnDestroy,
	Component,
	Optional,
	ChangeDetectorRef,
	Input,
	Renderer2,
	NgZone,
	ElementRef,
	ContentChildren,
	TemplateRef,
	QueryList,
	ContentChild,
	inject,
	signal,
	Signal,
	Injector,
	input,
	computed,
} from '@angular/core'
import { Subscription } from 'rxjs'
import { TrackByBasis } from '@lg/shared'
import { BreakpointObserver } from '@angular/cdk/layout'
import { Breakpoints } from '@angular/cdk/layout'
import { ChangeDetectionService } from '../services/change-detection.service.js'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import {
	BoInfoSymbol,
	BusinessObjectIdType,
	BusinessObjectTypeType,
	ModuleIdType,
	ProxyUtil,
	ScreenQualifiedNameAttribute,
	ValidationRules,
} from '@lg/shared'
import { LayoutService } from '../services/layout-service.js'
import { AppContext } from '../app-context.js'
import { AppTrpcServiceInjectionToken } from '../services/app-trpc.service.js'
import { ProcessService } from '../services/process.service.js'
import { AuthService } from '../services/auth-service.js'
import { DevBarService } from '../services/dev-bar.service.js'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControlManager } from '../data/form-control-manager.js'
import { FormDirective } from '../directives/form.directive.js'
import { AbstractControl } from '@angular/forms'

@Component({
	template: '',
})
export abstract class AbstractScreen
	extends TrackByBasis
	implements OnInit, OnDestroy
{
	@Input() @Optional() $formDirective: FormDirective | undefined
	@Input() @Optional() $disabled: boolean = false
	$ignoreValidationsInput = input(false, { alias: '$ignoreValidations' })
	$ignoreValidationsSignal = computed(
		() =>
			this.$ignoreValidationsInput() ||
			this.devBarService.isIgnoreValidations(),
	)
	get $ignoreValidations() {
		return this.$ignoreValidationsSignal()
	}
	@ContentChild(TemplateRef, { read: TemplateRef, static: true })
	template: TemplateRef<any>
	@ContentChildren(TemplateRef) contentTemplates: QueryList<TemplateRef<any>>
	private contentTemplateCache = new Map<string, TemplateRef<any>>()
	protected devBarService = inject(DevBarService)
	protected injector = inject(Injector)

	Object = Object
	JSON = JSON
	Math = Math
	navigator = navigator
	document = document

	$AppContext = AppContext
	subscriptions: Subscription[] = []
	onDestroyFns: (() => void)[] = []
	app: any
	output: any

	screen: any
	abstract get $inputs(): any
	$validators!: Record<number, ValidationRules>
	private memoizedSignals = new WeakMap<
		object,
		Map<string | number, Signal<any>>
	>()
	getMemoizedSignal(
		forObject: object,
		name: string,
		signalFactory: () => Signal<any>,
	) {
		if (!this.memoizedSignals.has(forObject)) {
			this.memoizedSignals.set(forObject, new Map())
		}
		const signals = this.memoizedSignals.get(forObject)!
		if (!signals.has(name)) {
			signals.set(name, signalFactory())
		}
		return signals.get(name)!
	}

	$qualifiedScreenName = ''

	protected dialogData = inject(MAT_DIALOG_DATA, { optional: true })
	protected dialogRef = inject(MatDialogRef, { optional: true })

	protected authService = inject(AuthService)
	protected route = inject(ActivatedRoute)
	protected router = inject(Router)
	protected breakpointObserver = inject(BreakpointObserver)
	protected translationService = inject(TranslationService)
	protected cdService = inject(ChangeDetectionService)
	protected cdRef = inject(ChangeDetectorRef)
	protected layoutService = inject(LayoutService)
	protected renderer = inject(Renderer2)
	protected zone = inject(NgZone)
	protected elementRef: ElementRef<HTMLElement> = inject(ElementRef)
	protected processService = inject(ProcessService)

	protected readonly queryParams = toSignal(this.route.queryParams)
	protected readonly $isFullyInitialized = signal(false)
	protected readonly $isVariablesInitialized = signal(false)
	protected $isOnInitCalled = false

	isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
		map((result) => result.matches),
		shareReplay(1),
	)

	ngOnInit() {
		const globalAny = globalThis as any
		if (!globalAny.$screen)
			Object.defineProperty(globalAny, '$screen', { get: () => this.screen })
		if (!globalAny.$app)
			Object.defineProperty(globalAny, '$app', { get: () => this.app })
		if (!globalAny.$output)
			Object.defineProperty(globalAny, '$output', { get: () => this.output })
		if (!globalAny.$api)
			Object.defineProperty(globalAny, '$api', {
				get: () => AppContext.injector.get(AppTrpcServiceInjectionToken),
			})
		this.subscriptions.push(
			// FrontendLanguageBridge.languageChanged$.subscribe(() =>
			// 	this.cdRef.detectChanges(),
			// ),
			// FrontendLanguageBridge.editMode$.subscribe(() =>
			// 	this.cdRef.detectChanges(),
			// ),
			this.devBarService.forceScreenRefreshSubject.subscribe(() =>
				this.cdRef.detectChanges(),
			),
			this.cdService.markViewDirty$.subscribe(() => {
				this.cdRef.markForCheck()
			}),
		)
		this.elementRef.nativeElement.setAttribute(
			ScreenQualifiedNameAttribute,
			this.$qualifiedScreenName,
		)
	}

	ngOnDestroy() {
		this.subscriptions.forEach((sub) => sub.unsubscribe())
		this.onDestroyFns.forEach((fn) => fn())
	}

	closeDialogAndReturn(result?: any) {
		result = ProxyUtil.getTarget(result)

		if (!this.dialogRef) {
			console.error('DialogRef not found when trying to close dialog')
		}

		this.dialogRef?.close(result)
	}

	getFormControl(
		obj: any,
		propName: string | number,
		componentId: number,
		scope: any,
		formDirective: FormDirective,
		disabled: boolean,
		ignoreValidations: boolean,
	) {
		if (!obj)
			throw new Error(
				`getFormControl: obj is null or undefined for Screen block #${this.screen.id}. Check whether the binding looks correct and possibly wrap the block with an If to make sure this cannot happen.`,
			)
		if (typeof obj !== 'object')
			throw new Error(
				`getFormControl: obj is not an object, but ${typeof obj} for Screen block #${this.screen.id}. Check whether the binding looks correct.`,
			)

		const validationRules =
			this.devBarService.isIgnoreValidations() || ignoreValidations
				? null
				: this.$validators[componentId]

		const formControl = FormControlManager.getFormControl(
			obj,
			propName,
			componentId,
			validationRules,
			scope,
			formDirective,
			undefined,
		)
		if (disabled != formControl.disabled) {
			if (disabled) {
				formControl.disable({ onlySelf: true })
			} else {
				formControl.enable({ onlySelf: true })
			}
		}
		return formControl
	}

	getDetachedFormControl(
		obj: object,
		propName: string,
		componentId: number,
		scope: any,
		formDirective: FormDirective,
		disabled: boolean,
		_ignoreValidations: boolean,
		initialValue: any,
	) {
		const formControl = FormControlManager.getFormControl(
			obj,
			propName,
			componentId,
			null,
			scope,
			formDirective,
			{
				getInitialValue: () => initialValue,
				onValueChange: () => {
					/* ignore */
				},
			},
		)
		if (disabled != formControl.disabled) {
			if (disabled) {
				formControl.disable({ onlySelf: true })
			} else {
				formControl.enable({ onlySelf: true })
			}
		}
		return formControl
	}

	private getAlternativeBindingPropertyOnObject(value: any) {
		if (value && typeof value == 'object' && !(value instanceof Date))
			return 'id' // in case of StaticEntity
		return undefined
	}

	getCorrectBindingObject(bindingObject: object, bindingProperty: string) {
		const value = Reflect.get(bindingObject, bindingProperty)
		const alternativeBindingProperty =
			this.getAlternativeBindingPropertyOnObject(value)
		// if(value && typeof value == 'object' && !(value instanceof Date)) return value // in case of StaticEntity
		return alternativeBindingProperty ? value : bindingObject
	}

	getCorrectBindingProperty(bindingObject: object, bindingProperty: string) {
		const value = Reflect.get(bindingObject, bindingProperty)
		const alternativeBindingProperty =
			this.getAlternativeBindingPropertyOnObject(value)
		// if(value && typeof value == 'object') return 'id' // in case of StaticEntity
		return alternativeBindingProperty || bindingProperty
	}

	getContentTemplate(name: string) {
		// console.log('getContentTemplate', name, this.contentTemplates?.length)
		if (!this.contentTemplateCache.has(name) && this.contentTemplates) {
			const templateRef = [...this.contentTemplates].find((templateRef) => {
				const templateAttrs = (templateRef as any)._declarationTContainer
					?.attrs as string[]
				const contentAreaKeyIdx = templateAttrs.indexOf('data-content-area')
				if (contentAreaKeyIdx > -1) {
					const templateName = templateAttrs[contentAreaKeyIdx + 1]
					if (templateName == name) return true
				}

				return false
			})
			if (templateRef) {
				this.contentTemplateCache.set(name, templateRef)
			}
		}
		return this.contentTemplateCache.get(name)
	}

	$log(...args: any[]) {
		console.log(...args)
		return args.map((arg) => arg?.toString())
	}

	$navigate(
		moduleId: ModuleIdType,
		screenId: BusinessObjectIdType,
		queryParams: object,
	) {
		const screenName =
			AppContext.appModuleId == moduleId ? screenId : `${moduleId}.${screenId}`
		this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
			// first fake navigation to "/"" to force Angular router to refresh
			this.router.navigate([screenName], {
				queryParams,
			})
		})
	}

	$getNavigationUrl(
		moduleId: ModuleIdType,
		screenId: BusinessObjectIdType,
		queryParams: object,
	) {
		const screenName =
			AppContext.appModuleId == moduleId ? screenId : `${moduleId}.${screenId}`
		const params = Object.entries(queryParams)
			.map((e) => `${e[0]}=${encodeURI(e[1])}`)
			.join('&')
		return params ? `${screenName}?${params}` : screenName
	}

	$isLinkPointingToCurrentScreen(
		moduleId: ModuleIdType,
		screenId: BusinessObjectIdType,
	) {
		const [_, appModuleId, _screenName] = window.location.pathname.split('/')
		let screenName = _screenName
		if (screenName && !screenName.includes('.')) {
			screenName = `${appModuleId}.${screenName}`
		} else if (!screenName) {
			screenName = AppContext.defaultScreen.join('.')
		}

		return screenName == `${moduleId}.${screenId}`
	}

	$dispatchInputEvent(element: HTMLElement) {
		const event = new KeyboardEvent('input', { bubbles: true })
		element.dispatchEvent(event)
	}

	$dispatchGenericEvent(element: HTMLElement, eventName: string) {
		const event = new Event(eventName, { bubbles: true })
		element.dispatchEvent(event)
	}

	$compare(a: any, b: any) {
		const boTypeA = a?.[BoInfoSymbol]?.boType as BusinessObjectTypeType
		const boTypeB = b?.[BoInfoSymbol]?.boType as BusinessObjectTypeType

		if (
			(boTypeA == 'StaticEntity' ||
				boTypeA == 'Entity' ||
				boTypeB == 'StaticEntity' ||
				boTypeB == 'Entity') &&
			a?.id &&
			b?.id
		) {
			a = a.id
			b = b.id
		}

		return a === b
	}

	$editInputValue(element: EventTarget) {
		if (!(element instanceof HTMLInputElement)) {
			console.error(
				'Cannot edit input value as element is not an HTMLInputElement:',
				element,
			)
			return
		}
		const openPrompt = (value: string, errorMessage: string | undefined) => {
			const promptText =
				(errorMessage ? `There was an error: ${errorMessage}\n\n` : '') +
				'Enter a calculation:'
			const expression = prompt(promptText, value)
			if (expression) {
				try {
					const value = Function(`return (${expression})`)()
					element.value = value
					this.$dispatchInputEvent(element)
				} catch (error: any) {
					openPrompt(expression, error.message)
				}
			}
		}
		openPrompt(element.value, undefined)
	}

	$hasErrors(formControl: AbstractControl) {
		return formControl.errors && Object.keys(formControl.errors).length > 0
	}

	$shouldShowErrors(formControl: AbstractControl) {
		return (
			this.$hasErrors(formControl) && (formControl.touched || formControl.dirty)
		)
	}
}
